import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sword, Shield, Eye, EyeOff, Globe, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { t, getStoredLang, setStoredLang } from "@/utils/i18n";

const LANGS = [
  { id: "it", label: "IT" }, { id: "en", label: "EN" }, { id: "es", label: "ES" },
  { id: "fr", label: "FR" }, { id: "de", label: "DE" }, { id: "pt", label: "PT" },
];

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lang, setLang] = useState(getStoredLang());
  const [regStatus, setRegStatus] = useState({ registrations_open: true, registration_mode: "automatic" });
  const { login, register, user, API } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  React.useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API}/auth/registration-status`);
        if (res.ok) {
          const data = await res.json();
          setRegStatus(data);
        }
      } catch (e) {
        /* non-critical */
      }
    };
    fetchStatus();
  }, [API]);

  const changeLang = (newLang) => { setLang(newLang); setStoredLang(newLang); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success(t(lang, 'welcome_back'));
        navigate("/");
      } else {
        if (!regStatus.registrations_open) {
          toast.error(lang === 'it' ? "Le iscrizioni sono temporaneamente chiuse dall'amministratore." : "Registrations are currently closed by the administrator.");
          return;
        }
        const res = await register(username, email, password);
        if (res?.status === "pending_approval") {
          toast.info(res.message || "Account registrato! In attesa di approvazione dell'amministratore.", { duration: 6000 });
          setIsLogin(true);
        } else {
          toast.success(t(lang, 'begin_legend'));
          navigate("/");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0c] relative overflow-hidden px-4">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c] via-[#1a1a1d] to-[#0a0a0c]" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#cfb53b] rounded-full opacity-[0.03] blur-[100px]" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#581c87] rounded-full opacity-[0.05] blur-[100px]" />

      <div className="relative z-10 w-full max-w-md" data-testid="auth-page">
        {/* Iter 91.1 — top toolbar: back-to-home + language switcher */}
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <Link
            to="/"
            data-testid="auth-back-home"
            className="inline-flex items-center gap-1.5 text-xs text-[#cfb53b]/70 hover:text-[#cfb53b] font-heading uppercase tracking-[0.2em] transition-colors"
          >
            <ArrowLeft size={14} /> {lang === 'it' ? 'Torna alla home' : 'Back to home'}
          </Link>
          <div className="flex gap-1 flex-wrap items-center" data-testid="lang-switcher">
            <Globe size={14} className="text-[#64748b] mr-1" />
            {LANGS.map(l => (
              <button
                key={l.id}
                data-testid={`ui-lang-${l.id}`}
                onClick={() => changeLang(l.id)}
                className={`text-xs px-2 py-1 rounded transition-colors font-mono ${
                  lang === l.id ? 'bg-[#cfb53b]/20 text-[#cfb53b]' : 'text-[#64748b] hover:text-[#cfb53b]'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Logo */}
        <div className="text-center mb-6 sm:mb-10 animate-fadeInUp">
          <img
            src="/logo.png"
            alt="Arcane Chronicles"
            className="w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-4 rounded-xl shadow-[0_0_40px_rgba(207,181,59,0.35)]"
            data-testid="auth-logo"
          />
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <Sword className="w-6 h-6 sm:w-8 sm:h-8 text-[#cfb53b]" />
            <h1 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-bold text-[#cfb53b] tracking-wider">
              Arcane Chronicles
            </h1>
            <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-[#cfb53b]" />
          </div>
          <p className="text-[#94a3b8] font-body text-sm sm:text-base">
            {isLogin ? t(lang, 'welcome_back') : t(lang, 'begin_legend')}
          </p>
        </div>

        {/* Form */}
        <div className="bg-[#1a1a1d]/80 backdrop-blur-xl border border-[#cfb53b]/20 rounded-lg p-5 sm:p-8 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          {!isLogin && !regStatus.registrations_open && (
            <div className="mb-4 p-3 bg-red-950/60 border border-red-500/40 rounded text-red-200 text-xs text-center font-heading">
              ⚠️ {lang === 'it' ? "Le iscrizioni pubbliche sono temporaneamente chiuse dall'amministratore." : "Public registrations are currently closed by the administrator."}
            </div>
          )}
          {!isLogin && regStatus.registrations_open && regStatus.registration_mode === "approval_required" && (
            <div className="mb-4 p-3 bg-amber-950/60 border border-amber-500/40 rounded text-amber-200 text-xs text-center font-heading">
              ℹ️ {lang === 'it' ? "La registrazione di nuovi account richiede l'approvazione di un amministratore prima del primo accesso." : "New account registrations require administrator approval before logging in."}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            {!isLogin && (
              <div>
                <Label htmlFor="username" className="text-[#cfb53b] font-heading text-sm">{t(lang, 'username')}</Label>
                <Input
                  id="username"
                  data-testid="auth-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t(lang, 'enter_name')}
                  required={!isLogin}
                  className="mt-1 bg-[#0a0a0c] border-[#cfb53b]/30 text-[#e2e8f0] placeholder:text-[#64748b] focus:border-[#cfb53b] focus:ring-[#cfb53b]/20"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-[#cfb53b] font-heading text-sm">{t(lang, 'email')}</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="mt-1 bg-[#0a0a0c] border-[#cfb53b]/30 text-[#e2e8f0] placeholder:text-[#64748b] focus:border-[#cfb53b] focus:ring-[#cfb53b]/20"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#cfb53b] font-heading text-sm">{t(lang, 'password')}</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  data-testid="auth-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="..."
                  required
                  className="bg-[#0a0a0c] border-[#cfb53b]/30 text-[#e2e8f0] placeholder:text-[#64748b] focus:border-[#cfb53b] focus:ring-[#cfb53b]/20 pr-10"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#cfb53b]">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="auth-submit-button"
              disabled={submitting}
              className="w-full bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading font-bold text-base sm:text-lg py-3 rounded-md transition-all hover:shadow-[0_0_20px_rgba(207,181,59,0.3)]"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-[#0a0a0c] border-t-transparent rounded-full animate-spin" />
                  {t(lang, 'loading')}
                </span>
              ) : (
                isLogin ? t(lang, 'enter_realm') : t(lang, 'begin_adventure')
              )}
            </Button>
          </form>

          <div className="mt-5 sm:mt-6 text-center">
            <button
              data-testid="auth-toggle-mode"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#94a3b8] hover:text-[#cfb53b] transition-colors text-xs sm:text-sm font-body"
            >
              {isLogin ? t(lang, 'no_account') : t(lang, 'already_account')}
            </button>
          </div>
        </div>
        {/* Legal footer */}
        <div className="mt-6 flex flex-wrap justify-center gap-x-3 gap-y-1 text-[10px] sm:text-xs text-[#64748b]" data-testid="legal-footer">
          <Link to="/about" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-about">About</Link>
          <span className="text-[#2d3748]">·</span>
          <Link to="/privacy" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-privacy">Privacy</Link>
          <span className="text-[#2d3748]">·</span>
          <Link to="/terms" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-terms">{(lang || "en").startsWith("it") ? "Termini" : "Terms"}</Link>
          <span className="text-[#2d3748]">·</span>
          <Link to="/cookies" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-cookies">Cookie</Link>
          <span className="text-[#2d3748]">·</span>
          <Link to="/contact" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-contact">{(lang || "en").startsWith("it") ? "Contatti" : "Contact"}</Link>
        </div>
      </div>
    </div>
  );
}
